/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */
import { Swiper, Navigation, A11y } from 'swiper';
import { breakpoints } from '../../../resources/scripts/config/breakpoints';

const CARD_IMAGE_WIDTHS = {
  xlPlus: 482,
  xl: 432,
  sm: 377,
  xs: 250,
};

const STATIC_THRESHOLDS = {
  twoCards: 250,
};

function getSlideWidth(viewportWidth = window.innerWidth) {
  if (viewportWidth > breakpoints.xlPlus) {
    return CARD_IMAGE_WIDTHS.xlPlus;
  }

  if (viewportWidth > breakpoints.xl) {
    return CARD_IMAGE_WIDTHS.xl;
  }

  if (viewportWidth > breakpoints.sm) {
    return CARD_IMAGE_WIDTHS.sm;
  }

  return CARD_IMAGE_WIDTHS.xs;
}

function getSpaceBetween(viewportWidth = window.innerWidth) {
  return viewportWidth >= breakpoints.md ? 32 : 24;
}

function getTotalSlidesWidth(slideCount, slideWidth, spaceBetween) {
  return (slideCount * slideWidth) + ((slideCount - 1) * spaceBetween);
}

function clearActiveCards(rootEl) {
  rootEl.querySelectorAll('.card-carousel-square__card.is-active').forEach((card) => {
    card.classList.remove('is-active');
  });
}

function destroySlideObserver(rootEl) {
  if (rootEl.cardCarouselSquareSlideObserver) {
    rootEl.cardCarouselSquareSlideObserver.disconnect();
    rootEl.cardCarouselSquareSlideObserver = null;
  }
}

function getSwiperContentWidth(swiperEl) {
  const styles = getComputedStyle(swiperEl);
  const paddingLeft = parseFloat(styles.paddingLeft) || 0;
  const paddingRight = parseFloat(styles.paddingRight) || 0;

  return swiperEl.clientWidth - paddingLeft - paddingRight;
}

function needsSlider(rootEl, slideCount) {
  if (slideCount <= 1) {
    return false;
  }

  const swiperEl = rootEl.querySelector('.card-carousel-square__swiper');

  if (!swiperEl) {
    return false;
  }

  const containerWidth = getSwiperContentWidth(swiperEl);
  const viewportWidth = window.innerWidth;
  const spaceBetween = getSpaceBetween(viewportWidth);

  if (slideCount === 2) {
    const cardWidth = (containerWidth - spaceBetween) / 2;

    return cardWidth < STATIC_THRESHOLDS.twoCards;
  }

  if (slideCount === 3) {
    const minCardWidth = getSlideWidth(viewportWidth);
    const totalMinWidth = getTotalSlidesWidth(3, minCardWidth, spaceBetween);

    return totalMinWidth > containerWidth;
  }

  const slideWidth = getSlideWidth(viewportWidth);
  const totalWidth = getTotalSlidesWidth(slideCount, slideWidth, spaceBetween);

  return totalWidth > containerWidth;
}

function initStaticLayout(rootEl, swiperEl, prevEl, nextEl) {
  rootEl.classList.remove('card-carousel-square--slider');
  swiperEl.classList.add('card-carousel-square__swiper--centered');

  [prevEl, nextEl].forEach((btn) => {
    btn.classList.add('disabled');
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
  });
}

function initSliderLayout(rootEl, swiperEl, prevEl, nextEl) {
  rootEl.classList.add('card-carousel-square--slider');
  swiperEl.classList.remove('card-carousel-square__swiper--centered');

  const swiper = new Swiper(swiperEl, {
    modules: [Navigation, A11y],
    slidesPerView: 'auto',
    spaceBetween: getSpaceBetween(),
    loop: false,
    watchOverflow: true,
    centerInsufficientSlides: true,
    slidesPerGroup: 1,
    breakpoints: {
      [breakpoints.md]: {
        spaceBetween: 32,
      },
    },
    navigation: {
      prevEl,
      nextEl,
      disabledClass: 'disabled',
    },
    a11y: {
      enabled: true,
      prevSlideMessage: 'Previous slide',
      nextSlideMessage: 'Next slide',
    },
  });

  // Handling focus during Tab/Shift+Tab navigation
  swiperEl.addEventListener('focusin', (e) => {
    const slide = e.target.closest('.swiper-slide');
    if (slide) {
      const index = Array.from(swiper.slides).indexOf(slide);
      if (index !== -1 && index !== swiper.activeIndex) {
        swiper.slideTo(index);
      }
    }
  });

  return swiper;
}

function initCardCarouselSquareRoot(rootEl) {
  const swiperEl = rootEl.querySelector('.card-carousel-square__swiper');

  if (!swiperEl) {
    return;
  }

  const prevEl = rootEl.querySelector('.card-carousel-square__nav--prev');
  const nextEl = rootEl.querySelector('.card-carousel-square__nav--next');

  if (!prevEl || !nextEl) {
    return;
  }

  const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;

  destroySlideObserver(rootEl);
  clearActiveCards(rootEl);

  if (swiperEl.swiper) {
    swiperEl.swiper.destroy(true, true);
  }

  // Resetting the state of the buttons before initialization
  [prevEl, nextEl].forEach((btn) => {
    btn.classList.remove('disabled');
    btn.disabled = false;
    btn.removeAttribute('aria-disabled');
    btn.tabIndex = 0;
  });

  if (needsSlider(rootEl, slideCount)) {
    initSliderLayout(rootEl, swiperEl, prevEl, nextEl);
    return;
  }

  initStaticLayout(rootEl, swiperEl, prevEl, nextEl);
}

function initAllCardCarouselSquares() {
  document.querySelectorAll('.card-carousel-square').forEach((rootEl) => {
    initCardCarouselSquareRoot(rootEl);
  });
}

let resizeTimeout;

function onResize() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    initAllCardCarouselSquares();
  }, 150);
}

document.addEventListener('DOMContentLoaded', () => {
  initAllCardCarouselSquares();
  window.addEventListener('resize', onResize);

  if (window.acf) {
    window.acf.addAction('render_block_preview/type=azds-card-carousel-square', () => {
      initAllCardCarouselSquares();
    });
  }
});
